import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Clock, ExternalLink, MessageSquare, Pencil } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "@/api/email";
import { getPilotComments, getPilotReviewMessage, savePilotReviewEmail, type ReviewComments, type ReviewMessage } from "@/api/pilotEditorial";
import { getCompany } from "@/api/companies";
import { PilotMessageComments } from "./PilotMessageComments";
import { PilotMessageHistory } from "./PilotMessageHistory";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { pilotPeople, pilotVariantLabel, pilotVariantKeys, type PilotRecord } from "@/lib/pilot";
import { reviewSteps, companyWebsite, reviewCompanyLinks, reviewDraftChanged, reviewParams, reviewSelection, type ReviewDraft, type ReviewSelection } from "@/lib/pilotReview";
import { cn } from "@/lib/utils";

/** One existing prospect and one stored email; metadata is not commercial copy. */
export function PilotReviewReader({ record, campaignId, slug, canEdit, onSaved }: {
  record: PilotRecord;
  campaignId: number;
  slug: string;
  canEdit: boolean;
  onSaved: () => Promise<void>;
}) {
  const [params, setParams] = useSearchParams();
  const people = useMemo(() => pilotPeople(record), [record]);
  const selection = useMemo(() => reviewSelection(people, params, record), [people, params, record]);
  const person = people.find((lead) => lead.code === selection.code);
  const [message, setMessage] = useState<ReviewMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState<ReviewComments | null>(null);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentsError, setCommentsError] = useState("");
  const [history, setHistory] = useState<ReviewMessage | null>(null);
  const [links, setLinks] = useState<{companyId: number; pappers: string | null; website: string | null; prism: string} | null>(null);
  const [linkError, setLinkError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const email = message?.person_code === selection.code && message.position === selection.position &&
    (selection.position !== 1 || message.variant === selection.variant) ? message : null;
  const [draft, setDraft] = useState<ReviewDraft | null>(null);
  const [pending, setPending] = useState<ReviewSelection | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const dirty = reviewDraftChanged(draft);
  const root = useRef<HTMLDivElement>(null);
  const alive = useRef(true);
  const [height, setHeight] = useState<number>();

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true); setError("");
    if (!selection.code) {setLoading(false); return;}
    void getPilotReviewMessage(slug, campaignId, selection).then((data) => {
      if (active) setMessage(data);
    }).catch((cause) => {if (active) {setMessage(null); const detail = errorMessage(cause); setError(detail.includes("Missing required value for Anciennete") ? "Aperçu alternatif indisponible : ce modèle exige une ancienneté qui n’est pas établie. Le texte attribué reste inchangé." : detail);}})
      .finally(() => {if (active) setLoading(false);});
    return () => {active = false;};
  }, [slug, campaignId, selection, record.editorial?.revision, refresh]);

  useEffect(() => {
    let active = true;
    setComments(null); setCommentsError("");
    if (!email || loading) return;
    void getPilotComments(slug, campaignId, selection).then((data) => {if (active) setComments(data);})
      .catch((cause) => {if (active) setCommentsError(errorMessage(cause));});
    return () => {active = false;};
  }, [slug, campaignId, selection, email, loading]);

  useEffect(() => {
    let active = true;
    setLinks(null); setLinkError("");
    if (!person?.company_id) {setLinkError("Société non rattachée"); return;}
    const companyId = person.company_id;
    void getCompany(slug, companyId).then((company) => {
      // This existing scoped read enforces company visibility permissions.
      if (active && company.id === companyId) setLinks({companyId, ...reviewCompanyLinks(slug, company), website: companyWebsite(company.domain)});
    }).catch(() => {if (active) setLinkError("Fiche indisponible ou accès non autorisé");});
    return () => {active = false;};
  }, [slug, person?.company_id]);

  // Use the space below the actual campaign navigation, including wrapped headers.
  useLayoutEffect(() => {
    const element = root.current;
    if (!element) return;
    const resize = () => setHeight(Math.max(320, window.innerHeight - element.getBoundingClientRect().top - window.scrollY - 32));
    resize();
    const observer = new ResizeObserver(resize);
    if (element.parentElement) observer.observe(element.parentElement);
    window.addEventListener("resize", resize);
    return () => { observer.disconnect(); window.removeEventListener("resize", resize); };
  }, []);

  useEffect(() => {
    if (!dirty) return;
    const preventLoss = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", preventLoss);
    return () => window.removeEventListener("beforeunload", preventLoss);
  }, [dirty]);

  function move(next: ReviewSelection) {
    setDraft(null);
    setPending(null);
    setHistory(null);
    setCommentsOpen(false);
    setError("");
    setParams(reviewParams(params, next), { replace: true });
  }

  function requestMove(next: ReviewSelection) {
    if (busy || (next.code === selection.code && next.position === selection.position && next.variant === selection.variant)) return;
    if (dirty) setPending(next);
    else move(next);
  }

  async function edit() {
    if (!person || !email || !canEdit || busy) return;
    setBusy(true);
    setError("");
    try {
      const current = await getPilotReviewMessage(slug, campaignId, selection);
      if (!alive.current) return;
      setMessage(current);
      setDraft({ ...selection, revision: current.revision, subject: current.subject, body: current.body,
        original: { subject: current.subject, body: current.body } });
    } catch (cause) {
      if (alive.current) setError(errorMessage(cause));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  async function save(next?: ReviewSelection) {
    if (!draft || !canEdit || busy) return;
    setBusy(true);
    setError("");
    try {
      await savePilotReviewEmail(slug, campaignId, draft);
      if (!alive.current) return;
      setDraft(null);
      setPending(null);
      await onSaved();
      if (!alive.current) return;
      setRefresh((n) => n + 1);
      if (next) move(next);
      toast.success("Email enregistré");
    } catch (cause) {
      // In particular, keep the text on optimistic-lock conflicts or failed saves.
      if (alive.current) setError(errorMessage(cause));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  if (!people.length) return <p className="p-6 text-sm text-slate-500">Aucun prospect dans cette campagne.</p>;

  return (
    <div ref={root} data-testid="pilot-review-reader" className="grid min-h-[320px] min-w-0 grid-cols-[minmax(9rem,15rem)_minmax(0,1fr)] gap-4" style={{ height }}>
      <aside aria-label={record.kind === "PILOT" ? "Prospects" : "Scénarios"} className="min-h-0 min-w-0 overflow-y-auto overscroll-contain rounded-plane bg-white p-2 shadow-plane">
        <ul className="space-y-1">
          {people.map((lead) => (
            <li key={lead.code}>
              <button aria-current={person?.code === lead.code ? "true" : undefined}
                disabled={busy} onClick={() => requestMove({ code: lead.code, position: selection.position })}
                className={cn("w-full rounded-lg px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 disabled:opacity-60",
                  person?.code === lead.code ? "bg-slate-900 text-white" : "text-slate-800 hover:bg-slate-50")}>
                <span className="block break-words text-sm font-medium leading-5">{lead.company}</span>
                <span className={cn("mt-0.5 block break-words text-xs leading-4", person?.code === lead.code ? "text-slate-300" : "text-slate-500")}>{lead.director}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section aria-label="Email du prospect" className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-plane bg-white shadow-plane">
        <nav aria-label="Étapes de la séquence" className="grid shrink-0 gap-1 border-b border-slate-100 p-2" style={{gridTemplateColumns: `repeat(${reviewSteps(record).length}, minmax(0, 1fr))`}}>
          {reviewSteps(record).map((step) => (
            <button key={step.position} aria-label={`${step.day} · ${step.label}`} aria-current={selection.position === step.position ? "step" : undefined}
              disabled={busy} onClick={() => requestMove({ code: selection.code, position: step.position, ...(step.position === 1 ? {variant: selection.variant ?? person?.variant} : {}) })}
              className={cn("min-w-0 rounded-lg px-1 py-2 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 disabled:opacity-60",
                selection.position === step.position ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50")}>
              <span className="block text-sm font-semibold">{step.day}</span>
              <span className="mt-0.5 block text-xs">{step.label}</span>
            </button>
          ))}
        </nav>
        <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-slate-100 px-6 py-2 text-xs">
          <span className="font-medium text-slate-700">{person?.company}</span>
          {record.profile === "CFO_US_GROWTH_V1" ? (links?.companyId === person?.company_id && links?.website && <a href={links.website} target="_blank" rel="noopener noreferrer" aria-label={`Site officiel : ${person?.company} (nouvel onglet)`} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900">Site officiel<ExternalLink className="h-3 w-3"/></a>) : <>{links?.companyId === person?.company_id && links?.pappers ?
            <a href={links.pappers} target="_blank" rel="noopener noreferrer" aria-label={`Pappers : ${person?.company} (nouvel onglet)`} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900">Pappers<ExternalLink className="h-3 w-3"/></a> :
            <button disabled title={linkError || (links ? "Aucun SIREN ou lien fiable disponible" : "Chargement de la société")} className="inline-flex items-center gap-1 text-slate-400">Pappers<ExternalLink className="h-3 w-3"/></button>}</>}
          {links?.companyId === person?.company_id && links?.prism ?
            <a href={links.prism} target="_blank" rel="noopener noreferrer" aria-label={`Fiche Prism : ${person?.company} (nouvel onglet)`} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900">Fiche Prism<ExternalLink className="h-3 w-3"/></a> :
            <button disabled title={linkError || "Chargement de la société"} className="inline-flex items-center gap-1 text-slate-400">Fiche Prism<ExternalLink className="h-3 w-3"/></button>}
          {selection.position === 1 && pilotVariantKeys(record).length > 1 && <div role="group" aria-label="Variante du premier email" className="ml-auto flex flex-wrap items-center gap-2">
            {pilotVariantKeys(record).map((variant) => <button key={variant} aria-pressed={selection.variant === variant} disabled={busy}
              onClick={() => requestMove({...selection, variant})} className={cn("rounded-md px-2 py-1.5", selection.variant === variant ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50")}>
              {pilotVariantLabel(record, variant)}
            </button>)}
            <span className="text-slate-500">{selection.variant === person?.variant ? "Attribuée" : "Aperçu alternatif"}</span>
          </div>}
        </div>
        {error && <p role="alert" className="shrink-0 border-b border-rose-100 bg-rose-50 px-5 py-2 text-sm text-rose-800">{error}</p>}

        {draft ? <form aria-label="Modifier cet email uniquement" className="flex min-h-0 flex-1 flex-col" onSubmit={(event) => { event.preventDefault(); void save(); }}>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
            <label className="block shrink-0 text-sm font-medium">Objet
              <input aria-label="Objet" autoFocus value={draft.subject} disabled={busy} required
                onChange={(event) => setDraft({ ...draft, subject: event.target.value })}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm font-normal focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400" />
            </label>
            <label className="flex min-h-[180px] flex-1 flex-col text-sm font-medium">Corps
              <textarea aria-label="Corps" value={draft.body} disabled={busy} required
                onChange={(event) => setDraft({ ...draft, body: event.target.value })}
                className="mt-2 min-h-[140px] w-full flex-1 resize-none rounded-lg border border-slate-200 p-3 text-[15px] font-normal leading-7 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400" />
            </label>
          </div>
          <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-100 px-6 py-3">
            <Button type="button" variant="ghost" disabled={busy} onClick={() => { if (dirty) setPending(selection); else setDraft(null); }}>Annuler</Button>
            <Button type="submit" disabled={busy || !dirty || !draft.subject.trim() || !draft.body.trim()}>{busy ? "Enregistrement…" : "Enregistrer"}</Button>
          </footer>
        </form> : <>
          <div key={`${selection.code}:${selection.position}:${selection.variant ?? ""}`} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-6 lg:px-8">
            {email && !loading ? <article aria-label="Email commercial" className="mx-auto max-w-[65ch] break-words text-[15px] leading-7 text-slate-800">
              <h2 className="mb-5 border-b border-slate-100 pb-4 text-base font-semibold text-slate-950">{email.subject}</h2>
              <div className="whitespace-pre-wrap">{email.body}</div>
            </article> : <p className="text-sm text-slate-500">{loading ? "Chargement de l’email…" : "Cet email n’est pas disponible."}</p>}
          </div>
          {record.editorial && email && <footer className="flex shrink-0 gap-2 border-t border-slate-100 px-6 py-3">
            {canEdit && <Button variant="outline" size="sm" disabled={busy || loading} onClick={() => void edit()}><Pencil className="h-3.5 w-3.5" />Modifier cet email</Button>}
            <Button variant="ghost" size="sm" disabled={busy || loading} onClick={() => {setCommentsOpen(false); setHistory(email);}}><Clock className="h-3.5 w-3.5"/>Historique</Button>
            <Button variant="ghost" size="sm" disabled={busy || loading || !comments} title={commentsError || undefined}
              onClick={() => {setHistory(null); setCommentsOpen(true);}}><MessageSquare className="h-3.5 w-3.5"/>Commentaires{comments && comments.comments.length > 0 ? ` (${comments.comments.length})` : ""}</Button>
          </footer>}
        </>}
      </section>

      {commentsOpen && comments && <PilotMessageComments data={comments} selection={selection} slug={slug} campaignId={campaignId}
        variantLabel={pilotVariantLabel(record, selection.variant ?? person?.variant ?? "A")} name={person?.director ?? "ce prospect"} onClose={() => setCommentsOpen(false)} onAdded={setComments}/>}
      {history && <PilotMessageHistory message={history} slug={slug} campaignId={campaignId} variantLabel={pilotVariantLabel(record, history.variant ?? "A")} name={person?.director ?? "ce prospect"} canEdit={canEdit}
        onClose={() => setHistory(null)} onRestored={async () => {await onSaved(); setRefresh((n) => n + 1);}}/>}

      <Dialog open={pending !== null} onOpenChange={(open) => { if (!open && !busy) setPending(null); }}>
        <DialogContent onEscapeKeyDown={(event) => { if (busy) event.preventDefault(); }} onInteractOutside={(event) => { if (busy) event.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle>Conserver vos modifications ?</DialogTitle>
            <DialogDescription>Seul cet email de {person?.director} sera modifié. Vous pouvez l’enregistrer ou abandonner le texte saisi avant de continuer.</DialogDescription>
          </DialogHeader>
          {error && <p role="alert" className="text-sm text-rose-800">{error}</p>}
          <DialogFooter className="flex-wrap gap-2 sm:space-x-0">
            <Button variant="ghost" disabled={busy} onClick={() => setPending(null)}>Rester sur cet email</Button>
            <Button variant="outline" disabled={busy} onClick={() => { if (pending) move(pending); }}>Abandonner</Button>
            <Button disabled={busy || !draft?.subject.trim() || !draft?.body.trim()} onClick={() => { if (pending) void save(pending); }}>{busy ? "Enregistrement…" : "Enregistrer et continuer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
